import FooterLinks from "@/components/FooterLinks";
import { Button } from "@/components/ui/button";
import MarketingTopBar from "@/components/MarketingTopBar";
import { useAuthModal } from "@/context/AuthModalContext";
import { ArrowRight } from "lucide-react";

const POWERED_BY =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_throttle-hub-6/artifacts/krvfdgqm_Powered%20by%20PLSR%20%28cropped%29.png";

/**
 * Shared shell for the public /learn/* marketing pages.
 * Hero is intentionally identical across pages for a consistent look.
 */
export default function LearnLayout({
  kicker,
  title,
  sub,
  heroImage,
  heroBgSize = "cover",
  heroBgPosition = "center",
  heroImageOpacity = 0.75,
  heroImageTranslateX = "0%",
  heroImageFlipX = false,
  heroFadeGradient = "linear-gradient(to right, hsl(240 8% 4%) 0%, hsl(240 8% 4% / 0.55) 55%, hsl(240 8% 4% / 0.10) 100%)",
  heroOverlayColor = "rgba(0,0,0,0.3)",
  ctaLabel = "Create your account",
  children,
  testId,
  bodyBg,
}) {
  const { openAuth } = useAuthModal();
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={testId}>
      <MarketingTopBar />

      {/* Hero — standardised height/spacing/typography */}
      <section className="relative flex min-h-[70vh] items-center overflow-hidden bg-background pt-56 pb-24 sm:pt-72 sm:pb-32">
        {/* Right-anchored 3:2 hero image */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
          <div className="relative h-full aspect-[3/2] max-w-[85vw]" style={{ transform: `translateX(${heroImageTranslateX})` }}>
            <div
              className="absolute inset-0 bg-no-repeat"
              style={{
                backgroundImage: `url(${heroImage})`,
                backgroundSize: heroBgSize,
                backgroundPosition: heroBgPosition,
                opacity: heroImageOpacity,
                transform: heroImageFlipX ? "scaleX(-1)" : "none",
              }}
            />
            {/* Black overlay (literal rgba so privacy browsers render it) */}
            <div className="pointer-events-none absolute inset-0" style={{ backgroundColor: heroOverlayColor }} />
            {/* Left fade into the black section background */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: heroFadeGradient,
              }}
            />
            {/* Soft top/bottom vignettes */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to bottom, hsl(240 8% 4% / 0.55) 0%, transparent 45%, hsl(240 8% 4% / 0.70) 100%)",
              }}
            />
          </div>
        </div>

        {/* Checkered-flag micro strip — matches the home hero */}
        <div className="checker absolute right-0 top-0 hidden h-full w-3 opacity-30 lg:block" />

        <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6">
          <div className="max-w-3xl animate-fade-up">
            {kicker && <span className="kicker">{kicker}</span>}
            <h1 className={`font-display text-5xl font-bold uppercase leading-[1] sm:text-6xl lg:text-7xl ${kicker ? "mt-4" : ""}`}>
              {title}
            </h1>
            {sub && <p className="mt-6 max-w-2xl text-lg sm:text-xl" style={{ color: "#d8dadf" }}>{sub}</p>}
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" onClick={() => openAuth("register")} className="h-14 px-10 text-lg bg-primary text-primary-foreground hover:bg-primary/90" data-testid="learn-primary-cta">
                {ctaLabel} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => openAuth("login")} className="h-14 px-10 text-lg border-border">Sign in</Button>
            </div>
          </div>
        </div>
        <div className="racing-line absolute bottom-0 left-0 right-0" />
      </section>

      <div className="overflow-x-hidden" style={bodyBg ? { backgroundColor: bodyBg } : undefined}>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6">{children}</div>
      </div>

      <section className="relative overflow-hidden border-t border-border">
        <div className="speed-hatch absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-3xl px-4 py-20 text-center sm:px-6">
          <h2 className="font-display text-4xl font-bold uppercase sm:text-5xl">
            Take your place on the <span className="text-gradient-primary-soft">grid</span>
          </h2>
          <Button size="lg" onClick={() => openAuth("register")} className="mt-8 bg-primary text-primary-foreground hover:bg-primary/90">
            {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="racing-line absolute bottom-0 left-0 right-0" />
      </section>

      <footer className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-10 sm:px-6">
        <img src={POWERED_BY} alt="Powered by PLSR" className="h-10 w-auto opacity-90" draggable={false} />
        <FooterLinks tone="light" />
      </footer>
    </div>
  );
}
